import { buildDefaultMulti } from './buildDefaultMulti';
import { Direction } from './direction';
import type { Line } from './Line';
import { MathObject } from './MathObject';
import type { Multi, MultiDependencies } from './Multi';

const MIDDLE_H = 7;
const MIDDLE_THICK = 2;
const EXTRA_W_FOR_FRACTION_BAR = 4;
const BLANK_PIXEL_PADDING = 1;

export class Fraction extends MathObject {
  numerator: Multi;
  denominator: Multi;
  autoCapturedNumerator = false;

  constructor(line: Line, numerator: Multi, denominator: Multi) {
    super();

    this.t = 'fraction';
    this.gmmName = 'fraction';

    this.numerator = numerator;
    this.denominator = denominator;
    this.line = line;

    this.children.push(this.numerator);
    this.numerator.parent = this;
    this.children.push(this.denominator);
    this.denominator.parent = this;
  }

  getPaintedCharCount(): number {
    return (
      this.numerator.getPaintedCharCount() +
      this.denominator.getPaintedCharCount()
    );
  }

  setEditable(b: boolean): void {
    this.numerator.setEditable(b);
    this.denominator.setEditable(b);
  }

  setFont(f: string): void {
    this.numerator.setFont(f);
    this.denominator.setFont(f);
  }

  setNumeratorAndDenominator(num: Multi, denom: Multi): void {
    this.children = [];
    this.children.push(num);
    this.children.push(denom);
    this.numerator = num;
    this.denominator = denom;
    num.parent = this;
    denom.parent = this;
  }

  mouseDownResponse(x: number, y: number): boolean {
    // Check the attempt getter to see if it is editable
    let parentIsLocked = false;

    if (
      this.line &&
      this.line.locationParent &&
      (this.line.locationParent as MathObject).e != undefined
    ) {
      parentIsLocked = !(this.line.locationParent as MathObject).e;
    }

    if (!this.line || !this.line.e || parentIsLocked) return false;
    const dir = x < this.viewportW / 2 ? 'right' : 'left';

    if (y < this.numerator.viewportH + MIDDLE_H / 2) {
      this.numerator.grabFocus(dir);
    } else this.denominator.grabFocus(dir);

    return true;
  }

  setLine(line: Line): void {
    this.line = line;
    this.numerator.setLine(line);
    this.denominator.setLine(line);
  }

  toXML(): string {
    return (
      '<f><num>' +
      this.numerator.toXML() +
      '</num><denom>' +
      this.denominator.toXML() +
      '</denom></f>'
    );
  }

  swapString(old: string, replacer: string): void {
    this.numerator.swapString(old, replacer);
    this.denominator.swapString(old, replacer);
  }

  grabFocus(direction: Direction): void {
    this.numerator.grabFocus(direction);
  }

  buildSizeRecursive(): void {
    this.numerator.buildSizeRecursive();
    this.denominator.buildSizeRecursive();
    this.buildSize();
  }

  buildSize(): void {
    let w = Math.max(this.numerator.viewportW, this.denominator.viewportW);

    w += EXTRA_W_FOR_FRACTION_BAR * 2 + 2 * BLANK_PIXEL_PADDING;
    let h = this.numerator.viewportH + this.denominator.viewportH;

    h += MIDDLE_H;
    this.setAllDim(w, h);
    this.setPositionsOfChildren();
  }

  getVertToBaseline(): number {
    return this.numerator.viewportH + MIDDLE_H / 2;
  }

  setPositionsOfChildren(): void {
    const horizontalSurplusNum =
      this.viewportW - EXTRA_W_FOR_FRACTION_BAR * 2 - this.numerator.viewportW;

    this.numerator.viewportX =
      EXTRA_W_FOR_FRACTION_BAR + horizontalSurplusNum / 2;
    this.numerator.viewportY = 0;

    const horizontalSurplusDenom =
      this.viewportW -
      EXTRA_W_FOR_FRACTION_BAR * 2 -
      this.denominator.viewportW;

    this.denominator.viewportX =
      EXTRA_W_FOR_FRACTION_BAR + horizontalSurplusDenom / 2;
    this.denominator.viewportY = this.numerator.viewportH + MIDDLE_H;
  }

  childSizeChanged(): void {
    this.buildSize();
    (this.parent as MathObject)?.childSizeChanged();
  }

  paintMe(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.strokeStyle = this.fillStyle || 'black';
    ctx.beginPath();
    ctx.lineWidth = MIDDLE_THICK;
    ctx.moveTo(BLANK_PIXEL_PADDING, this.getVertToBaseline());
    ctx.lineTo(this.viewportW - BLANK_PIXEL_PADDING, this.getVertToBaseline());
    ctx.stroke();
    ctx.restore();
  }

  add(tile: MathObject, denom: any): void {
    if (!denom) {
      this.numerator.add(tile);
    } else {
      this.denominator.add(tile);
    }
  }
}

export function buildFraction(
  problemJS: MultiDependencies,
  line: Line,
  num?: Multi,
  denom?: Multi
): Fraction {
  return new Fraction(
    line,
    num || buildDefaultMulti(problemJS, line),
    denom || buildDefaultMulti(problemJS, line)
  );
}
